import {
  mergeAttributes,
  Node,
  NodeViewWrapper,
  ReactNodeViewRenderer
} from '@tiptap/react';
import { icons } from 'lucide-react';

import CustomDatePicker from './CustomDatePicker';

export interface TagAttributes {
  color: string;
  iconName: keyof typeof icons;
  text: string;
}

const TagComponent: Parameters<typeof ReactNodeViewRenderer>[0] = ({
  node,
  updateAttributes
}) => {
  const { color, iconName, text } = node.attrs as TagAttributes;

  // eslint-disable-next-line import/namespace
  const Icon = icons[iconName];

  return (
    <NodeViewWrapper
      className={`tag ${color}`}
      contentEditable={false}
    >
      <Icon
        className="shrink-0"
        size={16}
      />
      {text.endsWith(': Custom') ? (
        <CustomDatePicker
          onUpdate={newText => {
            updateAttributes({ text: newText });
          }}
          text={text}
        />
      ) : (
        <div>@{text}</div>
      )}
    </NodeViewWrapper>
  );
};

const Tag = Node.create({
  addAttributes() {
    return {
      color: {
        parseHTML: element => element.getAttribute('data-color'),
        renderHTML: (attrs: TagAttributes) => ({
          'data-color': attrs.color
        })
      },
      iconName: {
        parseHTML: element => element.getAttribute('data-icon-name'),
        renderHTML: (attrs: TagAttributes) => ({
          'data-icon-name': attrs.iconName
        })
      },
      text: {
        parseHTML: element => element.getAttribute('data-text'),
        renderHTML: (attrs: TagAttributes) => ({
          'data-text': attrs.text
        })
      }
    };
  },
  addCommands() {
    return {
      insertTag:
        (text, color, iconName) =>
        ({ commands }) =>
          commands.insertContent([
            {
              attrs: { color, iconName, text },
              type: this.name
            },
            {
              text: ' ',
              type: 'text'
            }
          ])
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(TagComponent);
  },
  atom: true,
  group: 'inline',
  inline: true,
  name: 'tag',
  parseHTML() {
    return [{ tag: 'div.tag' }];
  },
  renderHTML({ HTMLAttributes, node }) {
    return [
      'div',
      mergeAttributes({ class: 'tag' }, HTMLAttributes),
      ['p', node.textContent]
    ];
  }
});

export default Tag;
